import { Field, Form, Formik } from 'formik'
import React, { useEffect } from 'react'
import * as Yup from 'yup'

import { Button, Fieldset, Input, Textarea, Upload, UploadedAsset } from '@fourthwall/components'

import type { App } from '../../types'
import * as S from './BasicInformationTab.styled'
import { CredentialDisplay } from './components/CredentialDisplay/CredentialDisplay'
import { useUpdatePlatformAppClientDetails } from './hooks/useUpdatePlatformAppClientDetails'
import { useUpdatePlatformAppClientIcon } from './hooks/useUpdatePlatformAppClientIcon'

type BasicInformationFormValues = {
  name: string
  description: string
  settingsUrl?: string
  learnMoreUrl?: string
  icon?: string
}

type BasicInformationTabProps = {
  app: App
}

const validationSchema = Yup.object().shape({
  name: Yup.string().required('App name is required'),
  description: Yup.string(),
  settingsUrl: Yup.string().url('Must be a valid URL').optional().nullable(),
  learnMoreUrl: Yup.string().url('Must be a valid URL').optional().nullable(),
  icon: Yup.string(),
})

const getInitialValues = (app: App): BasicInformationFormValues => ({
  name: app?.name || '',
  description: app?.description || '',
  settingsUrl: app?.settingsUrl,
  learnMoreUrl: app?.learnMoreUrl,
  icon: app?.iconUrl || '',
})

export const BasicInformationTab = ({ app }: BasicInformationTabProps) => {
  const { updatePlatformAppClientDetails, isLoading } = useUpdatePlatformAppClientDetails()
  const { updatePlatformAppClientIcon, isLoading: isIconLoading } = useUpdatePlatformAppClientIcon()

  const handleSubmit = (values: BasicInformationFormValues) => {
    if (!app.id) return
    updatePlatformAppClientDetails([
      { clientId: app.id },
      {
        displayName: values.name,
        description: values.description,
        settingsUrl: values.settingsUrl,
        learnMoreUrl: values.learnMoreUrl,
      },
    ])
  }

  return (
    <Formik<BasicInformationFormValues>
      initialValues={getInitialValues(app)}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
    >
      {({ submitForm, setFieldValue, values }) => {
        useEffect(() => {
          setFieldValue('icon', app.iconUrl || '')
        }, [app.iconUrl, setFieldValue])

        return (
          <Form>
            <S.Container>
              <Fieldset>
                <Fieldset.Row>
                  <Fieldset.Item>
                    <Field
                      component={Input}
                      name="name"
                      label="App Name"
                      placeholder="Enter app name"
                    />
                  </Fieldset.Item>
                </Fieldset.Row>
                <Fieldset.Row>
                  <Fieldset.Item>
                    <div>
                      <S.Label htmlFor="description__textarea">Description</S.Label>
                      <Field
                        component={Textarea}
                        rows={3}
                        name="description"
                        label="Description"
                        placeholder="Enter app description"
                      />
                    </div>
                  </Fieldset.Item>
                </Fieldset.Row>
                <Fieldset.Row>
                  <Fieldset.Item>
                    <Field
                      component={Input}
                      name="settingsUrl"
                      label="Settings URL"
                      placeholder="https://your-app.com/settings"
                    />
                  </Fieldset.Item>
                </Fieldset.Row>
                <Fieldset.Row>
                  <Fieldset.Item>
                    <Field
                      component={Input}
                      name="learnMoreUrl"
                      label="Learn More URL"
                      placeholder="https://your-app.com/learn-more"
                    />
                  </Fieldset.Item>
                </Fieldset.Row>
                {/* Icon Upload Section */}
                <Fieldset.Row>
                  <Fieldset.Item>
                    <S.Label>App Icon</S.Label>
                    {values.icon ? (
                      <div style={{ width: '200px' }}>
                        <UploadedAsset
                          id="app-icon"
                          src={values.icon}
                          onRemove={() => setFieldValue('icon', '')}
                        >
                          <S.IconContainer>
                            <S.IconPreview src={app.iconUrl} alt="App icon" />
                          </S.IconContainer>
                        </UploadedAsset>
                      </div>
                    ) : (
                      <Upload
                        accept={{
                          'image/jpeg': ['.jpg', '.jpeg'],
                          'image/png': ['.png'],
                          'image/svg+xml': ['.svg'],
                          'image/webp': ['.webp'],
                        }}
                        title="Drop icon or browse"
                        size="medium"
                        maxSize={524288} // 512KB
                        isUploading={isIconLoading}
                        onFileDrop={async (files) => {
                          if (files?.[0]) {
                            updatePlatformAppClientIcon([{ clientId: app.id }, files[0]])
                          }
                        }}
                      />
                    )}
                    <S.IconInfo>
                      Recommended: Square image, at least 200x200px. Max size: 512KB.
                    </S.IconInfo>
                  </Fieldset.Item>
                </Fieldset.Row>
                {/* Credentials Section */}
                <Fieldset.Row>
                  <Fieldset.Item>
                    <S.SectionLabel>Credentials</S.SectionLabel>
                  </Fieldset.Item>
                </Fieldset.Row>
                <Fieldset.Row>
                  <Fieldset.Item>
                    <CredentialDisplay
                      label="HMAC Key"
                      copyable
                      onReveal={async () => {
                        return app.hmacKey || ''
                      }}
                    />
                  </Fieldset.Item>
                </Fieldset.Row>
              </Fieldset>
              <S.ButtonContainer>
                <Button
                  label="Save Changes"
                  appearance="primary"
                  onClick={submitForm}
                  disabled={isLoading}
                  loading={isLoading}
                />
              </S.ButtonContainer>
            </S.Container>
          </Form>
        )
      }}
    </Formik>
  )
}
